import { Check, LightningIcon, TrendUpIcon } from '@phosphor-icons/react';
import React from 'react';
import { getTimezoneOffsetString } from '../../../../lib/utils';
import type { SelectedAccount, SourceType } from '../interface';
import { Badge } from 'flowbite-react';
import { useTranslation } from 'react-i18next';

interface SourceCardProps {
    type: SourceType;
    platformName: string;
    platformIcon?: string | null;
    accounts: SelectedAccount[];
    className?: string;
}

const SourceCard: React.FC<SourceCardProps> = ({
    type,
    platformName,
    platformIcon,
    accounts,
    className = '',
}) => {
    const { t } = useTranslation();
    const isSpending = type === 'spending';
    const labelColor = isSpending ? 'text-indigo-600 dark:text-indigo-400' : 'text-emerald-600 dark:text-emerald-400';
    const IconComponent = isSpending ? LightningIcon : TrendUpIcon;

    // Grouping for "Multiple Sources" display
    const platformCounts = accounts.reduce((acc, curr) => {
        const platform = curr.platform || 'unknown';
        acc[platform] = (acc[platform] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const uniquePlatforms = Object.keys(platformCounts);
    const isMultiple = uniquePlatforms.length > 1;

    return (
        <div
            className={`bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-2xl p-4 shadow-md transition-all ${className}`}
        >
            {/* Label */}
            <div className="flex items-center gap-2 mb-4 justify-between w-full" >
                <h4 className={`flex items-center gap-2 text-xs font-bold ${labelColor} uppercase tracking-widest`}>
                    <div className={`p-1.5 rounded-lg ${isSpending ? 'bg-indigo-50' : 'bg-emerald-50'}`}>
                        <IconComponent size={14} weight="bold" className={labelColor} />
                    </div>
                    {isSpending ? t('configurations.integrations.spendingChannels') : t('configurations.integrations.revenueSources')}
                    <Badge color={isSpending ? 'indigo' : 'success'} size="xs" className="rounded-full">
                        {accounts.length}
                    </Badge>
                </h4>
                {/* <div className="flex items-center  gap-2">
                    <div className={`p-1.5 rounded-lg ${isSpending ? 'bg-indigo-50' : 'bg-emerald-50'}`}>
                        <IconComponent size={14} weight="bold" className={labelColor} />
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${labelColor}`}>
                        {isSpending ? 'Spending Source' : 'Revenue Source'}
                    </span>
                </div> */}
                <div className="relative">
                    {isMultiple ? (
                        <div className="flex gap-1 -space-x-3 mr-3">
                            {uniquePlatforms.slice(0, 3).map((p, key) => {
                                const acc = accounts.find(a => a.platform === p);
                                const icon = acc?.platformIcon || platformIcon;
                                if (!icon) return null;
                                return (
                                    <div key={p} className="relative rounded-xl overflow-hidden bg-white dark:bg-neutral-800 shadow-lg border border-gray-200 dark:border-neutral-700" style={{ zIndex: uniquePlatforms.slice(0, 3).length - key }}>
                                        <img
                                            src={icon}
                                            alt={p}
                                            className="w-8 h-8 object-contain p-1"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        platformIcon && (
                            <div className="bg-white dark:bg-neutral-800 shadow-lg border border-gray-200 dark:border-neutral-700 rounded-xl">
                                <img
                                    src={platformIcon}
                                    alt={platformName}
                                    className="w-8 h-8 object-contain p-1"
                                />
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* Platform Summary Header */}
            {/* <div className="flex items-center mb-3">
                <div className="relative">
                    {isMultiple ? (
                        <div className="flex gap-1 -space-x-3 mr-3">
                            {uniquePlatforms.slice(0, 3).map((p, key) => {
                                const acc = accounts.find(a => a.platform === p);
                                return (
                                    <div key={p} className="relative rounded-xl overflow-hidden bg-white dark:bg-neutral-800 shadow-lg border border-gray-200 dark:border-neutral-700" style={{ zIndex: uniquePlatforms.slice(0, 3).length - key }}>
                                        <img
                                            src={acc?.platformIcon || platformIcon}
                                            alt={p}
                                            className="w-10 h-10 object-contain p-1"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-neutral-800 shadow-lg border border-gray-200 dark:border-neutral-700 rounded-xl">
                            <img
                                src={platformIcon}
                                alt={platformName}
                                className="w-10 h-10 object-contain p-1"
                            />
                        </div>
                    )}
                </div>
                <div className="flex-1 pt-1">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
                        {platformName}
                    </h4>
                    <p className="text-xs text-gray-500 font-medium mt-1">
                        {accounts.length} integration{accounts.length !== 1 ? 's' : ''} connected
                    </p>
                </div>
            </div> */}

            {/* Detailed Account List */}
            {accounts.length > 0 && (
                <div className="space-y-2 max-h-[calc(100dvh-535px)] overflow-y-auto">
                    {accounts.map((account) => (
                        <div
                            key={account.id}
                            className={`group flex items-start gap-3 p-3 rounded-xl border transition-all border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 h-full shadow-sm min-w-[450px]`}
                        >
                            <div className="relative mt-0.5">
                                {account.platformIcon ? (
                                    <div className="w-8 h-8 rounded-lg bg-white dark:bg-neutral-700 p-1 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
                                        <img src={account.platformIcon} alt="" className="w-full h-full object-contain" />
                                    </div>
                                ) : (
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-neutral-700`}>
                                        <Check size={16} weight="bold" className="text-gray-400 group-hover:text-secondary" />
                                    </div>
                                )}
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-neutral-800 flex items-center justify-center shadow-sm">
                                    <Check size={8} weight="bold" className="text-white" />
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">
                                        {account.name}
                                    </p>
                                </div>

                                <div className="mt-1.5 grid grid-cols-1 gap-y-1 text-[10px] text-gray-500 dark:text-neutral-400">
                                    {account.id && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium text-gray-600 dark:text-neutral-400">{t('configurations.integrations.id')}:</span>
                                            <span className="font-mono">{account.id}</span>
                                        </div>
                                    )}
                                    {account.email && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.email')}:</span>
                                            <span className="truncate">{account.email}</span>
                                        </div>
                                    )}
                                    {account.currencyCode && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.currency')}:</span>
                                            <span>{account.currencyCode}</span>
                                        </div>
                                    )}
                                    {account.timezone && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.timezoneLab')}</span>
                                            <span className="truncate">{account.timezone}, UTC{getTimezoneOffsetString(account.timezone)}</span>
                                        </div>
                                    )}
                                    {account.keyId && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.keyIdLabel')}</span>
                                            <span className="font-mono">{account.keyId}</span>
                                        </div>
                                    )}
                                    {account.issuerId && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.issuerIdLabel')}</span>
                                            <span className="truncate font-mono">{account.issuerId}</span>
                                        </div>
                                    )}
                                    {account.serviceAccount && (
                                        <div className="flex items-center gap-1">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">{t('configurations.integrations.service')}</span>
                                            <span className="truncate">{account.serviceAccount}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SourceCard;
